import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Phone, Mail, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const closeTimer = useRef(null);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => { setIsMobileMenuOpen(false); setShopOpen(false); }, [location]);

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop', dropdown: true },
    { name: 'Our Story', path: '/about' },
    { name: 'Help', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const openShop = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setShopOpen(true); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setShopOpen(false), 160); };

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Utility bar */}
      <div className="hidden sm:block bg-dark-900 text-dark-300 text-[13px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="tracking-wide">Free USPS shipping on every US order &middot; 2&ndash;4 day delivery</p>
          <div className="flex items-center gap-6">
            <a href={phoneHref} className="flex items-center gap-1.5 hover:text-white transition-colors"><Phone className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}</a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="hidden lg:flex items-center gap-1.5 hover:text-white transition-colors"><Mail className="w-3.5 h-3.5" /> {CONTACT_INFO.email}</a>
          </div>
        </div>
      </div>

      {/* Main row */}
      <div className="bg-white/95 backdrop-blur border-b border-dark-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[76px] gap-4">
            <Logo />

            <nav className="hidden lg:flex items-center gap-9">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                if (link.dropdown) {
                  return (
                    <div key={link.path} className="relative" onMouseEnter={openShop} onMouseLeave={scheduleClose}>
                      <Link to={link.path} className={`flex items-center gap-1 text-[15px] font-medium transition-colors ${isActive || shopOpen ? 'text-athletic-600' : 'text-dark-700 hover:text-athletic-600'}`}>
                        {link.name} <ChevronDown className={`w-4 h-4 transition-transform ${shopOpen ? 'rotate-180' : ''}`} />
                      </Link>
                      {shopOpen && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[560px]" onMouseEnter={openShop} onMouseLeave={scheduleClose}>
                          <div className="bg-white rounded-2xl shadow-elevated border border-dark-100 p-3 grid grid-cols-2 gap-1.5 animate-fade-in">
                            {categories.map((cat) => (
                              <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="group flex items-start gap-3 p-3 rounded-xl hover:bg-dark-50 transition-colors">
                                <span className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-dark-100"><img src={cat.image} alt={cat.name} className="w-full h-full object-cover" /></span>
                                <span className="min-w-0">
                                  <span className="block text-sm font-semibold text-dark-900 group-hover:text-athletic-600 transition-colors">{cat.name}</span>
                                  <span className="block text-xs text-dark-400 line-clamp-1">{cat.productCount} product{cat.productCount > 1 ? 's' : ''}</span>
                                </span>
                              </Link>
                            ))}
                            <Link to="/shop" className="col-span-2 mt-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-athletic-600 text-white text-sm font-semibold hover:bg-athletic-700 transition-colors">
                              View all products <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link key={link.path} to={link.path} className={`text-[15px] font-medium transition-colors ${isActive ? 'text-athletic-600' : 'text-dark-700 hover:text-athletic-600'}`}>
                    {link.name}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative flex items-center gap-2 h-11 px-4 rounded-lg border border-dark-200 text-dark-800 hover:border-athletic-400 hover:text-athletic-600 transition-colors"
                aria-label="Cart"
              >
                <ShoppingBag className="w-5 h-5" />
                <span className="hidden sm:inline text-sm font-semibold">Cart</span>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-athletic-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-lg flex items-center justify-center text-dark-900 bg-dark-100 hover:bg-dark-200"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-dark-100 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className="flex items-center justify-between px-4 py-3 text-base font-medium text-dark-700 hover:bg-dark-50 hover:text-athletic-600 rounded-lg">
                  {link.name}<ChevronRight className="w-4 h-4 text-dark-300" />
                </Link>
              ))}
              <div className="pt-3 border-t border-dark-100 mt-3">
                <p className="px-4 text-xs uppercase tracking-[0.2em] text-dark-400 mb-2">Categories</p>
                {categories.map((cat) => (
                  <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="block px-4 py-2.5 text-sm text-dark-600 hover:bg-dark-50 hover:text-athletic-600 rounded-lg">{cat.name}</Link>
                ))}
              </div>
              <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-lg bg-athletic-600 text-white font-semibold justify-center">
                <Phone className="w-4 h-4" /> Call {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
